<template>
  <section class="py-10 bg-white">
    <div class="max-w-6xl mx-auto px-6">
      <!-- 헤더 -->
      <div class="mb-10">
        <h2 class="text-[32px] font-extrabold text-slate-900 tracking-tight mb-2">물개 커뮤니티</h2>
        <p class="text-[16px] text-slate-500 font-medium">분양 정보부터 후기까지, 함께 이야기해요</p>
      </div>

      <!-- 로딩 -->
      <div v-if="loading" class="py-12 text-center text-slate-400 text-[14px]">
        로딩중...
      </div>

      <!-- 5개 카테고리 그리드 -->
      <div v-else class="grid grid-cols-5 gap-4 max-lg:grid-cols-3 max-md:grid-cols-2 max-sm:flex max-sm:gap-3 max-sm:overflow-x-auto max-sm:pb-2 scrollbar-hide">
        <div
          v-for="category in categories"
          :key="category.id"
          @click="goToPost(category)"
          class="flex-shrink-0 max-sm:w-[200px] bg-white rounded-2xl p-5 border border-slate-100 cursor-pointer hover:shadow-md hover:border-blue-200 transition-all"
        >
          <!-- 카테고리 라벨 -->
          <span :class="[
            'inline-block px-2.5 py-1 text-[11px] font-bold rounded mb-3',
            category.colorClass
          ]">
            {{ category.label }}
          </span>

          <!-- 글 제목 -->
          <h3 class="text-[15px] font-semibold text-slate-900 line-clamp-2 leading-snug mb-3 min-h-[42px]">
            {{ category.post?.title || '아직 글이 없습니다' }}
          </h3>

          <!-- 하단 정보 -->
          <div class="flex items-center gap-3 text-[12px] text-slate-400">
            <span class="flex items-center gap-1">
              <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              {{ category.post?.commentCount || 0 }}
            </span>
            <span class="flex items-center gap-1">
              <svg class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
              </svg>
              {{ category.post?.likeCount || 0 }}
            </span>
            <span v-if="category.post" class="ml-auto text-slate-300">
              {{ category.post.timeAgo }}
            </span>
          </div>
        </div>
      </div>

      <!-- 하단 버튼 -->
      <div class="text-center mt-8">
        <router-link
          to="/community"
          class="inline-flex items-center gap-1 text-[15px] font-semibold text-slate-500 hover:text-slate-900 hover:bg-slate-100 px-5 py-2.5 rounded-xl transition-all"
        >
          커뮤니티 전체보기
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
        </router-link>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useCommunity, COMMUNITY_CATEGORIES } from '@/composables/main/useCommunity'

const router = useRouter()
const { getPosts } = useCommunity()

const loading = ref(true)

// 카테고리별 색상
const categoryColors = {
  free: 'bg-slate-100 text-slate-600',
  question: 'bg-blue-50 text-blue-600',
  review: 'bg-amber-50 text-amber-600',
  info: 'bg-green-50 text-green-600',
  tip: 'bg-purple-50 text-purple-600'
}

// '전체' 카테고리 제외하고 5개만
const categories = ref(
  COMMUNITY_CATEGORIES
    .filter(cat => cat.id !== 'all')
    .map(cat => ({
      ...cat,
      colorClass: categoryColors[cat.id] || 'bg-slate-100 text-slate-600',
      post: null
    }))
)

// 게시글 상세로 이동
const goToPost = (category) => {
  if (category.post?.id) {
    router.push(`/community/${category.post.id}`)
  } else {
    // 글이 없으면 해당 카테고리 목록으로
    router.push(`/community?category=${category.id}`)
  }
}

// 시간 포맷
const formatTimeAgo = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  const now = new Date()
  const diff = Math.floor((now - date) / 1000)

  if (diff < 60) return '방금 전'
  if (diff < 3600) return `${Math.floor(diff / 60)}분 전`
  if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`
  if (diff < 604800) return `${Math.floor(diff / 86400)}일 전`
  return date.toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })
}

onMounted(async () => {
  try {
    // 각 카테고리별 최신 글 1개씩 조회 (전체 제외)
    const filteredCategories = COMMUNITY_CATEGORIES.filter(cat => cat.id !== 'all')
    const promises = filteredCategories.map(cat =>
      getPosts({ category: cat.id, pageSize: 1 })
    )
    const results = await Promise.all(promises)

    results.forEach((result, index) => {
      if (result.posts && result.posts.length > 0) {
        const post = result.posts[0]
        categories.value[index].post = {
          ...post,
          timeAgo: formatTimeAgo(post.createdAt)
        }
      }
    })
  } catch (error) {
    console.error('커뮤니티 로딩 실패:', error)
  } finally {
    loading.value = false
  }
})
</script>

<style scoped>
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
